import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';
import {
  X, Compass, Zap, Route, BookOpen, Map, ChevronRight, ChevronLeft,
  CheckCircle2, Circle, Send, Mail, FileText, Users, Calendar,
  ExternalLink, Play, SkipForward, Sparkles, MapPin, CalendarClock,
  Building2, Truck, Loader2, DollarSign, Receipt, ShoppingCart,
  Calculator, PlusCircle, CreditCard, TrendingUp, Package,
  ClipboardList, BarChart3, HelpCircle, Layers, Gavel, FileCheck,
  Palette, Camera, Upload, FolderOpen, Settings, Search,
  ArrowRight, Target, Lightbulb, GripVertical, Eye, EyeOff, MessageSquare, Star
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { computeJourneyProgress, getPhaseItems } from './project/journey-constants';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useBlueprint } from '../contexts/BlueprintContext';
import { useHelpIcons } from '../contexts/HelpIconContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Tab configuration
const TABS = [
  { id: 'actions', label: 'Actions', icon: Zap, description: 'Quick actions for this page' },
  { id: 'journey', label: 'Journey', icon: Route, description: 'Project workflow steps' },
  { id: 'learn', label: 'Learn', icon: BookOpen, description: 'How to use this page' },
  { id: 'map', label: 'Map', icon: Map, description: 'System overview' },
];

// Quick Actions configuration with descriptions
const QUICK_ACTIONS = {
  // ============================================
  // FINANCIAL CONTEXTS
  // ============================================
  financials: {
    label: 'Financial Actions',
    icon: DollarSign,
    description: 'You\'re in the Financials section. These actions help you manage money flow - invoicing clients, tracking expenses, and monitoring payments.',
    actions: [
      { id: 'create-invoice', label: 'Create Invoice', icon: Receipt, route: '/invoices/new', description: 'Bill a client for completed work or milestones' },
      { id: 'log-expense', label: 'Log Expense', icon: CreditCard, action: 'expense', description: 'Record a project cost or business expense' },
      { id: 'create-po', label: 'Create Purchase Order', icon: ShoppingCart, route: '/procurement', description: 'Order materials or services from a vendor' },
      { id: 'record-payment', label: 'Record Payment', icon: DollarSign, action: 'payment', description: 'Log a payment received from a client' },
      { id: 'view-reports', label: 'Financial Reports', icon: BarChart3, route: '/reports/financial', description: 'View profit/loss, cash flow, and other reports' },
    ]
  },
  estimate: {
    label: 'Estimate Actions',
    icon: Calculator,
    description: 'You\'re working on Estimates. Create detailed cost breakdowns, send proposals to clients, and convert approved estimates into invoices.',
    actions: [
      { id: 'generate-rfp', label: 'Generate RFP', icon: Send, action: 'rfp', description: 'Create a Request for Proposal to send to contractors' },
      { id: 'create-contract', label: 'Create Contract', icon: FileCheck, action: 'contract', description: 'Generate a contract from this estimate' },
      { id: 'send-to-client', label: 'Send to Client', icon: ExternalLink, action: 'send-estimate', description: 'Email this estimate/proposal to the client' },
      { id: 'create-invoice', label: 'Invoice from Estimate', icon: Receipt, action: 'invoice-estimate', description: 'Convert estimate line items into an invoice' },
      { id: 'export-pdf', label: 'Export PDF', icon: FileText, action: 'export-pdf', description: 'Download a PDF version for printing or sharing' },
    ]
  },
  invoices: {
    label: 'Invoice Actions',
    icon: Receipt,
    description: 'You\'re in Invoices. Create, send, and track client invoices. Record payments and manage billing schedules.',
    actions: [
      { id: 'create-invoice', label: 'New Invoice', icon: PlusCircle, action: 'new-invoice', description: 'Create a new client invoice' },
      { id: 'send-invoice', label: 'Send Invoice', icon: Send, action: 'send', description: 'Email invoice to client' },
      { id: 'record-payment', label: 'Record Payment', icon: DollarSign, action: 'payment', description: 'Log a payment received' },
      { id: 'create-reminder', label: 'Send Reminder', icon: Mail, action: 'reminder', description: 'Send payment reminder to client' },
      { id: 'export-pdf', label: 'Export PDF', icon: FileText, action: 'export', description: 'Download invoice as PDF' },
    ]
  },
  jobcosting: {
    label: 'Job Costing Actions',
    icon: BarChart3,
    description: 'You\'re in Job Costing. Track actual costs vs budget, monitor profit margins, and identify cost overruns.',
    actions: [
      { id: 'log-cost', label: 'Log Cost', icon: CreditCard, action: 'log-cost', description: 'Record an expense against this project' },
      { id: 'compare-budget', label: 'Budget vs Actual', icon: TrendingUp, action: 'compare', description: 'View budget comparison report' },
      { id: 'cost-report', label: 'Cost Report', icon: BarChart3, action: 'report', description: 'Generate detailed cost breakdown' },
      { id: 'allocate-cost', label: 'Allocate Cost', icon: Calculator, action: 'allocate', description: 'Distribute cost across categories' },
    ]
  },
  changeorders: {
    label: 'Change Order Actions',
    icon: ClipboardList,
    description: 'You\'re in Change Orders. Document scope changes, get client approval, and update project budget.',
    actions: [
      { id: 'create-co', label: 'New Change Order', icon: PlusCircle, action: 'new-co', description: 'Create a new change order' },
      { id: 'send-for-approval', label: 'Send for Approval', icon: Send, action: 'send', description: 'Send to client for signature' },
      { id: 'apply-to-budget', label: 'Apply to Budget', icon: DollarSign, action: 'apply', description: 'Update project budget with approved CO' },
      { id: 'export-pdf', label: 'Export PDF', icon: FileText, action: 'export', description: 'Download change order as PDF' },
    ]
  },
  purchaseorders: {
    label: 'Purchase Order Actions',
    icon: ShoppingCart,
    description: 'You\'re in Purchase Orders. Create orders, track deliveries, and manage vendor communications.',
    actions: [
      { id: 'create-po', label: 'New PO', icon: PlusCircle, action: 'new-po', description: 'Create a new purchase order' },
      { id: 'send-to-vendor', label: 'Send to Vendor', icon: Send, action: 'send', description: 'Email PO to vendor' },
      { id: 'receive-items', label: 'Receive Items', icon: Package, action: 'receive', description: 'Log items received' },
      { id: 'track-status', label: 'Track Status', icon: TrendingUp, action: 'track', description: 'Check delivery status' },
    ]
  },
  bids: {
    label: 'Bid Actions',
    icon: Gavel,
    description: 'You\'re managing Bids. Evaluate contractor proposals, award work, and convert winning bids into purchase orders.',
    actions: [
      { id: 'award-bid', label: 'Award Bid', icon: CheckCircle2, action: 'award', description: 'Select this contractor as the winning bidder' },
      { id: 'create-po', label: 'Create PO from Bid', icon: ShoppingCart, action: 'po-from-bid', description: 'Generate a purchase order from the awarded bid' },
      { id: 'compare-bids', label: 'Compare Bids', icon: Layers, action: 'compare', description: 'View side-by-side comparison of all bids' },
      { id: 'request-revision', label: 'Request Revision', icon: Send, action: 'revision', description: 'Ask contractor to revise their bid' },
      { id: 'generate-rfp', label: 'New RFP', icon: PlusCircle, action: 'new-rfp', description: 'Create a new Request for Proposal' },
    ]
  },
  procurement: {
    label: 'Procurement Actions',
    icon: Package,
    description: 'You\'re in Procurement. Order materials, track deliveries, and manage vendor relationships.',
    actions: [
      { id: 'create-po', label: 'New Purchase Order', icon: ShoppingCart, action: 'new-po', description: 'Create a new order to a vendor' },
      { id: 'receive-items', label: 'Receive Items', icon: Truck, action: 'receive', description: 'Log items that have arrived at the job site' },
      { id: 'track-delivery', label: 'Track Delivery', icon: TrendingUp, action: 'track', description: 'Check status of pending deliveries' },
      { id: 'vendor-invoice', label: 'Log Vendor Invoice', icon: Receipt, action: 'vendor-invoice', description: 'Record an invoice received from a vendor' },
      { id: 'reorder', label: 'Reorder Items', icon: Package, action: 'reorder', description: 'Quickly reorder items from past orders' },
    ]
  },

  // ============================================
  // PROJECT MANAGEMENT CONTEXTS
  // ============================================
  project: {
    label: 'Project Actions',
    icon: FolderOpen,
    description: 'You\'re in a Project Hub. Manage this specific project - add expenses, schedule meetings, upload files, and capture materials.',
    actions: [
      { id: 'log-expense', label: 'Log Expense', icon: CreditCard, action: 'expense', description: 'Record a cost for this project' },
      { id: 'add-meeting', label: 'Schedule Meeting', icon: Users, action: 'meeting', description: 'Set up a meeting with client or team' },
      { id: 'upload-file', label: 'Upload File', icon: Upload, action: 'upload', description: 'Add drawings, documents, or images' },
      { id: 'add-clip', label: 'Add Material', icon: Palette, action: 'clip', description: 'Save a product or material to this project' },
      { id: 'site-photo', label: 'Add Site Photo', icon: Camera, action: 'photo', description: 'Upload photos from the job site' },
    ]
  },
  projectinfo: {
    label: 'Project Info Actions',
    icon: Building2,
    description: 'You\'re viewing Project Info. Update client details, project address, status, and team assignments.',
    actions: [
      { id: 'edit-project', label: 'Edit Project', icon: Settings, action: 'edit', description: 'Update project details' },
      { id: 'add-contact', label: 'Add Contact', icon: Users, action: 'add-contact', description: 'Add client or team member' },
      { id: 'update-status', label: 'Update Status', icon: CheckCircle2, action: 'status', description: 'Change project status' },
      { id: 'set-address', label: 'Set Address', icon: MapPin, action: 'address', description: 'Update project location' },
    ]
  },
  journey: {
    label: 'Journey Actions',
    icon: Route,
    description: 'You\'re in Project Journey. Track progress through workflow phases, complete milestones, and see what\'s next.',
    actions: [
      { id: 'complete-step', label: 'Complete Step', icon: CheckCircle2, action: 'complete', description: 'Mark current step as done' },
      { id: 'skip-step', label: 'Not Needed', icon: SkipForward, action: 'skip', description: 'Mark the current step not needed' },
      { id: 'add-task', label: 'Add Task', icon: PlusCircle, action: 'add-task', description: 'Create task for current phase' },
      { id: 'view-timeline', label: 'View Timeline', icon: Calendar, action: 'timeline', description: 'See full project timeline' },
    ]
  },
  actions: {
    label: 'Action Items',
    icon: CheckCircle2,
    description: 'You\'re in Action Items. Create tasks, assign to team, set due dates, and track completion.',
    actions: [
      { id: 'create-action', label: 'New Action', icon: PlusCircle, action: 'new', description: 'Create a new action item' },
      { id: 'assign-action', label: 'Assign Action', icon: Users, action: 'assign', description: 'Assign to team member' },
      { id: 'set-due-date', label: 'Set Due Date', icon: Calendar, action: 'due-date', description: 'Set or change due date' },
      { id: 'mark-complete', label: 'Mark Complete', icon: CheckCircle2, action: 'complete', description: 'Mark action as done' },
    ]
  },
  progress: {
    label: 'Progress Log Actions',
    icon: Camera,
    description: 'You\'re in Progress Log. Document site visits, upload photos, and track project advancement.',
    actions: [
      { id: 'add-entry', label: 'Add Progress Entry', icon: PlusCircle, action: 'new-entry', description: 'Log work completed today' },
      { id: 'upload-photos', label: 'Upload Photos', icon: Camera, action: 'photos', description: 'Add site progress photos' },
      { id: 'note-issue', label: 'Note Issue', icon: HelpCircle, action: 'issue', description: 'Document a problem or concern' },
      { id: 'generate-report', label: 'Generate Report', icon: FileText, action: 'report', description: 'Create progress report' },
    ]
  },
  schedule: {
    label: 'Schedule Actions',
    icon: Calendar,
    description: 'You\'re in Project Schedule. Manage milestones, set deadlines, and coordinate task dependencies.',
    actions: [
      { id: 'add-milestone', label: 'Add Milestone', icon: Target, action: 'milestone', description: 'Create a project milestone' },
      { id: 'set-deadline', label: 'Set Deadline', icon: CalendarClock, action: 'deadline', description: 'Set or update a deadline' },
      { id: 'link-tasks', label: 'Link Tasks', icon: ArrowRight, action: 'link', description: 'Create task dependencies' },
      { id: 'export-gantt', label: 'Export Gantt', icon: FileText, action: 'export', description: 'Download schedule as PDF' },
    ]
  },
  meetings: {
    label: 'Meeting Actions',
    icon: Users,
    description: 'You\'re in Meeting Notes. Document meetings, record decisions, and track action items.',
    actions: [
      { id: 'new-meeting', label: 'New Meeting', icon: PlusCircle, action: 'new', description: 'Create meeting notes' },
      { id: 'record-audio', label: 'Record Audio', icon: Play, action: 'record', description: 'Start audio recording' },
      { id: 'add-attendees', label: 'Add Attendees', icon: Users, action: 'attendees', description: 'Add meeting participants' },
      { id: 'create-action', label: 'Create Action Item', icon: CheckCircle2, action: 'action', description: 'Add task from meeting' },
    ]
  },
  team: {
    label: 'Team Actions',
    icon: Users,
    description: 'You\'re in Project Team. Manage team members, contractors, and project roles.',
    actions: [
      { id: 'add-member', label: 'Add Team Member', icon: PlusCircle, action: 'add', description: 'Add someone to the team' },
      { id: 'assign-role', label: 'Assign Role', icon: Settings, action: 'role', description: 'Set team member role' },
      { id: 'send-invite', label: 'Send Invite', icon: Mail, action: 'invite', description: 'Invite to project portal' },
      { id: 'view-contacts', label: 'View Contacts', icon: Users, route: '/contacts', description: 'Go to contacts page' },
    ]
  },
  emails: {
    label: 'Email Actions',
    icon: Mail,
    description: 'You\'re in Project Emails. View correspondence, compose messages, and track communications.',
    actions: [
      { id: 'compose', label: 'Compose Email', icon: Send, action: 'compose', description: 'Write a new email' },
      { id: 'use-template', label: 'Use Template', icon: FileText, action: 'template', description: 'Start from email template' },
      { id: 'search', label: 'Search Emails', icon: Search, action: 'search', description: 'Search email history' },
      { id: 'archive', label: 'Archive Email', icon: FolderOpen, action: 'archive', description: 'Archive selected emails' },
    ]
  },

  // ============================================
  // DESIGN CONTEXTS
  // ============================================
  clips: {
    label: 'Material Actions',
    icon: Palette,
    description: 'You\'re in Clips / Materials. Manage product selections, create specifications, and add items to estimates.',
    actions: [
      { id: 'add-clip', label: 'Add Material', icon: PlusCircle, action: 'new-clip', description: 'Save a new product or material' },
      { id: 'import-clips', label: 'Import from Library', icon: FolderOpen, action: 'import', description: 'Copy materials from your master library' },
      { id: 'create-spec', label: 'Create Spec Sheet', icon: FileText, action: 'spec-sheet', description: 'Generate a specification document' },
      { id: 'request-quote', label: 'Request Quote', icon: Send, action: 'quote', description: 'Ask vendors for pricing on selected items' },
      { id: 'add-to-estimate', label: 'Add to Estimate', icon: Calculator, action: 'to-estimate', description: 'Include this material in the project estimate' },
    ]
  },
  moodboards: {
    label: 'Mood Board Actions',
    icon: Palette,
    description: 'You\'re in Mood Boards. Create visual presentations, add clips, and share with clients.',
    actions: [
      { id: 'create-board', label: 'New Mood Board', icon: PlusCircle, action: 'new', description: 'Create a new mood board' },
      { id: 'add-clips', label: 'Add Clips', icon: Layers, action: 'add-clips', description: 'Add materials to this board' },
      { id: 'add-text', label: 'Add Text/Label', icon: FileText, action: 'text', description: 'Add text annotation' },
      { id: 'export-pdf', label: 'Export PDF', icon: FileText, action: 'export', description: 'Download as PDF' },
      { id: 'share-client', label: 'Share with Client', icon: ExternalLink, action: 'share', description: 'Send to client portal' },
    ]
  },
  paint: {
    label: 'Paint Actions',
    icon: Palette,
    description: 'You\'re in Paint Specifications. Select colors, assign to rooms, and create paint schedules.',
    actions: [
      { id: 'add-color', label: 'Add Color', icon: PlusCircle, action: 'add', description: 'Add a paint color selection' },
      { id: 'assign-room', label: 'Assign to Room', icon: Building2, action: 'assign', description: 'Set color for a room' },
      { id: 'create-schedule', label: 'Create Schedule', icon: FileText, action: 'schedule', description: 'Generate paint schedule' },
      { id: 'calculate-qty', label: 'Calculate Quantity', icon: Calculator, action: 'calculate', description: 'Estimate paint needed' },
    ]
  },
  scope: {
    label: 'Scope Builder Actions',
    icon: FileText,
    description: 'You\'re in Scope Builder. Define work items, specifications, and exclusions for proposals.',
    actions: [
      { id: 'add-room', label: 'Add Room', icon: Building2, action: 'add-room', description: 'Add a new room to scope' },
      { id: 'add-item', label: 'Add Scope Item', icon: PlusCircle, action: 'add-item', description: 'Add work item to room' },
      { id: 'add-exclusion', label: 'Add Exclusion', icon: HelpCircle, action: 'exclusion', description: 'Note what\'s not included' },
      { id: 'generate-doc', label: 'Generate Document', icon: FileText, action: 'generate', description: 'Create scope of work document' },
    ]
  },
  plans: {
    label: 'Plans & Takeoff Actions',
    icon: Layers,
    description: 'You\'re in Floor Plans / Takeoff. Upload drawings, take measurements, and calculate quantities.',
    actions: [
      { id: 'upload-plan', label: 'Upload Plan', icon: Upload, action: 'upload', description: 'Add a new floor plan' },
      { id: 'set-scale', label: 'Set Scale', icon: Settings, action: 'scale', description: 'Calibrate drawing scale' },
      { id: 'measure', label: 'Take Measurement', icon: Calculator, action: 'measure', description: 'Measure distance or area' },
      { id: 'export', label: 'Export Measurements', icon: FileText, action: 'export', description: 'Download takeoff data' },
    ]
  },
  drawings: {
    label: 'Drawing Control Actions',
    icon: Layers,
    description: 'You\'re in Drawing Control. Manage versions, distribute drawings, and track approvals.',
    actions: [
      { id: 'upload', label: 'Upload Drawing', icon: Upload, action: 'upload', description: 'Add new drawing' },
      { id: 'new-revision', label: 'New Revision', icon: PlusCircle, action: 'revision', description: 'Upload revision' },
      { id: 'distribute', label: 'Distribute', icon: Send, action: 'distribute', description: 'Send to team/contractors' },
      { id: 'approve', label: 'Mark Approved', icon: CheckCircle2, action: 'approve', description: 'Approve drawing' },
    ]
  },
  rfis: {
    label: 'RFI Actions',
    icon: HelpCircle,
    description: 'You\'re in RFIs & Submittals. Create requests, track responses, and manage approvals.',
    actions: [
      { id: 'create-rfi', label: 'New RFI', icon: PlusCircle, action: 'new-rfi', description: 'Create new RFI' },
      { id: 'respond', label: 'Respond to RFI', icon: Send, action: 'respond', description: 'Add response' },
      { id: 'log-submittal', label: 'Log Submittal', icon: FileText, action: 'submittal', description: 'Record submittal received' },
      { id: 'approve', label: 'Approve Submittal', icon: CheckCircle2, action: 'approve', description: 'Approve submittal' },
    ]
  },
  quality: {
    label: 'Quality Control Actions',
    icon: CheckCircle2,
    description: 'You\'re in Quality Control. Manage punch lists, inspections, and issue resolution.',
    actions: [
      { id: 'add-punch', label: 'Add Punch Item', icon: PlusCircle, action: 'add', description: 'Create punch list item' },
      { id: 'assign', label: 'Assign to Contractor', icon: Users, action: 'assign', description: 'Assign item for resolution' },
      { id: 'add-photo', label: 'Add Photo', icon: Camera, action: 'photo', description: 'Document the issue' },
      { id: 'mark-resolved', label: 'Mark Resolved', icon: CheckCircle2, action: 'resolve', description: 'Mark as complete' },
    ]
  },

  // ============================================
  // TOOLS CONTEXTS
  // ============================================
  designai: {
    label: 'Design AI Actions',
    icon: Sparkles,
    description: 'You\'re in Design Companion AI. Ask questions, get recommendations, and explore design options.',
    actions: [
      { id: 'ask', label: 'Ask Question', icon: HelpCircle, action: 'ask', description: 'Ask design question' },
      { id: 'suggest-materials', label: 'Suggest Materials', icon: Palette, action: 'materials', description: 'Get material recommendations' },
      { id: 'analyze-space', label: 'Analyze Space', icon: Building2, action: 'analyze', description: 'Get space planning advice' },
      { id: 'generate-concept', label: 'Generate Concept', icon: Lightbulb, action: 'concept', description: 'Create design concepts' },
    ]
  },
  electrical: {
    label: 'Panel Builder Actions',
    icon: Zap,
    description: 'You\'re in Electrical Panel Builder. Design panel schedules, calculate loads, and generate diagrams.',
    actions: [
      { id: 'add-circuit', label: 'Add Circuit', icon: PlusCircle, action: 'add', description: 'Add new circuit' },
      { id: 'calculate-load', label: 'Calculate Load', icon: Calculator, action: 'calculate', description: 'Calculate total load' },
      { id: 'generate-diagram', label: 'Generate Diagram', icon: FileText, action: 'diagram', description: 'Create panel diagram' },
      { id: 'export', label: 'Export Schedule', icon: FileText, action: 'export', description: 'Download panel schedule' },
    ]
  },
  preengineering: {
    label: 'Pre-Engineering Actions',
    icon: Calculator,
    description: 'You\'re in Pre-Engineering Tools. Run calculations for HVAC, structural, and plumbing.',
    actions: [
      { id: 'hvac-calc', label: 'HVAC Calculation', icon: Calculator, action: 'hvac', description: 'Calculate heating/cooling loads' },
      { id: 'structural', label: 'Structural Estimate', icon: Calculator, action: 'structural', description: 'Estimate structural needs' },
      { id: 'plumbing', label: 'Plumbing Sizing', icon: Calculator, action: 'plumbing', description: 'Size plumbing systems' },
      { id: 'generate-report', label: 'Generate Report', icon: FileText, action: 'report', description: 'Create calculation report' },
    ]
  },

  // ============================================
  // COMMUNICATION CONTEXTS
  // ============================================
  messages: {
    label: 'Message Actions',
    icon: MessageSquare,
    description: 'You\'re in Project Messages. Add notes, tag team members, and track conversations.',
    actions: [
      { id: 'add-note', label: 'Add Note', icon: PlusCircle, action: 'note', description: 'Add a project note' },
      { id: 'tag-member', label: 'Tag Member', icon: Users, action: 'tag', description: 'Mention team member' },
      { id: 'pin-message', label: 'Pin Message', icon: Target, action: 'pin', description: 'Pin important note' },
      { id: 'search', label: 'Search Messages', icon: Search, action: 'search', description: 'Search message history' },
    ]
  },
  activity: {
    label: 'Activity Actions',
    icon: BarChart3,
    description: 'You\'re in Activity Log. View all project events, filter by type, and export reports.',
    actions: [
      { id: 'filter', label: 'Filter Activity', icon: Search, action: 'filter', description: 'Filter by date or type' },
      { id: 'export', label: 'Export Log', icon: FileText, action: 'export', description: 'Download activity report' },
      { id: 'view-details', label: 'View Details', icon: Eye, action: 'details', description: 'See full activity details' },
    ]
  },
  portals: {
    label: 'Portal Actions',
    icon: ExternalLink,
    description: 'You\'re in Client Portals. Configure access, share content, and manage client views.',
    actions: [
      { id: 'create-portal', label: 'Create Portal Link', icon: PlusCircle, action: 'create', description: 'Generate new portal link' },
      { id: 'configure', label: 'Configure Access', icon: Settings, action: 'configure', description: 'Set visible sections' },
      { id: 'share-moodboard', label: 'Share Mood Board', icon: Palette, action: 'share-mood', description: 'Add mood board to portal' },
      { id: 'track-views', label: 'Track Activity', icon: Eye, action: 'track', description: 'See client views' },
    ]
  },
  documents: {
    label: 'Document Actions',
    icon: FolderOpen,
    description: 'You\'re in File Control. Upload, organize, and share project documents.',
    actions: [
      { id: 'upload', label: 'Upload File', icon: Upload, action: 'upload', description: 'Add new file' },
      { id: 'create-folder', label: 'Create Folder', icon: FolderOpen, action: 'folder', description: 'Create new folder' },
      { id: 'share', label: 'Share File', icon: Send, action: 'share', description: 'Share with team or client' },
      { id: 'download', label: 'Download', icon: FileText, action: 'download', description: 'Download selected files' },
    ]
  },

  // ============================================
  // PIPELINE / CRM CONTEXTS
  // ============================================
  pipeline: {
    label: 'Pipeline Actions',
    icon: Users,
    description: 'You\'re in the Lead Pipeline. Manage leads from inquiry to project conversion.',
    actions: [
      { id: 'add-lead', label: 'New Lead', icon: PlusCircle, action: 'new-lead', description: 'Add a new lead' },
      { id: 'move-stage', label: 'Move Stage', icon: ArrowRight, action: 'move', description: 'Move lead to next stage' },
      { id: 'schedule-call', label: 'Schedule Call', icon: Calendar, action: 'call', description: 'Schedule follow-up call' },
      { id: 'send-email', label: 'Send Email', icon: Mail, action: 'email', description: 'Send email to lead' },
      { id: 'convert', label: 'Convert to Project', icon: FolderOpen, action: 'convert', description: 'Convert won lead to project' },
    ]
  },
  contacts: {
    label: 'Contact Actions',
    icon: Users,
    description: 'You\'re in Contacts. Manage clients, contractors, and vendors.',
    actions: [
      { id: 'add-contact', label: 'New Contact', icon: PlusCircle, action: 'new', description: 'Add a new contact' },
      { id: 'import', label: 'Import Contacts', icon: Upload, action: 'import', description: 'Import from CSV' },
      { id: 'send-email', label: 'Send Email', icon: Mail, action: 'email', description: 'Email selected contacts' },
      { id: 'export', label: 'Export List', icon: FileText, action: 'export', description: 'Download contact list' },
    ]
  },

  // ============================================
  // DEFAULT / DASHBOARD
  // ============================================
  default: {
    label: 'Quick Actions',
    icon: Zap,
    description: 'Welcome to your dashboard! These are common actions to help you get started. Navigate to a specific area for more contextual options.',
    actions: [
      { id: 'new-project', label: 'New Project', icon: PlusCircle, route: '/projects/new', description: 'Start a new design project' },
      { id: 'log-expense', label: 'Log Expense', icon: CreditCard, action: 'expense', description: 'Record a business expense' },
      { id: 'create-invoice', label: 'Create Invoice', icon: Receipt, route: '/invoices/new', description: 'Bill a client for work' },
      { id: 'view-tasks', label: 'My Tasks', icon: ClipboardList, route: '/tasks', description: 'See your to-do list' },
      { id: 'contacts', label: 'Contacts', icon: Users, route: '/contacts', description: 'Manage clients and vendors' },
    ]
  }
};

// Get step icon for Journey
const getStepIcon = (actionType) => {
  const icons = {
    email: Send,
    document: FileText,
    meeting: Users,
    site_visit: MapPin,
    milestone: Calendar,
    external: ExternalLink,
    contractor: Building2,
    supplier: Truck,
    default: Circle
  };
  return icons[actionType] || icons.default;
};

export default function CompassDrawer({ hideTab = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { currentBlueprint, routeBlueprint, fetchBlueprintById, allBlueprints } = useBlueprint();
  
  // Panel state
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('actions');
  const [buttonY, setButtonY] = useState(30); // Default position closer to Calc tab (at 20%)
  const [isDraggingButton, setIsDraggingButton] = useState(false);
  const buttonRef = useRef(null);
  
  // Listen for toggle events from FloatingTabGroup
  useEffect(() => {
    const handleToggle = () => {
      setIsOpen(prev => !prev);
    };
    window.addEventListener('toggle-compass', handleToggle);
    return () => window.removeEventListener('toggle-compass', handleToggle);
  }, []);
  
  // Emit compass state changes for FloatingTabGroup
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('compass-state-change', { detail: { isOpen } }));
  }, [isOpen]);
  
  // Journey state
  const [project, setProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [emailData, setEmailData] = useState({ to: '', subject: '', body: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [scheduleEmail, setScheduleEmail] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [recipientType, setRecipientType] = useState('client');
  const [showNotNeededDialog, setShowNotNeededDialog] = useState(false);
  const [notNeededReason, setNotNeededReason] = useState('');
  
  // Learn/Search state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Determine current context
  const isProjectRoute = location.pathname.match(/^\/projects\/[^/]+/);
  const currentProjectId = projectId || location.pathname.split('/')[2];
  
  const currentContext = useMemo(() => {
    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    
    // Check URL path first for dedicated pages
    if (path.includes('/financials') || path.includes('/invoices') || path.includes('/expenses')) {
      return 'financials';
    }
    if (path.includes('/procurement')) return 'procurement';
    if (path.includes('/estimates')) return 'estimate';
    if (path.includes('/bids')) return 'bids';
    if (path.includes('/floor-plans') || path.includes('/takeoff')) return 'plans';
    if (path.includes('/drawings')) return 'drawings';
    if (path.includes('/rfis')) return 'rfis';
    if (path.includes('/scope-builder')) return 'scope';
    if (path.includes('/quality-control') || path.includes('/punch')) return 'quality';
    if (path.includes('/purchase-orders')) return 'purchaseorders';
    if (path.includes('/design-companion')) return 'designai';
    
    // Check tab parameter for Project Hub tabs
    if (tab) {
      if (tab === 'clips') return 'clips';
      if (tab === 'moodboards' || tab === 'moods') return 'moodboards';
      if (tab === 'paint') return 'paint';
      if (tab === 'journey') return 'journey';
      if (tab === 'actions') return 'actions';
      if (tab === 'progress') return 'progress';
      if (tab === 'team') return 'team';
      if (tab === 'emails') return 'emails';
      if (tab === 'schedule') return 'schedule';
      if (tab === 'meetings') return 'meetings';
      if (tab === 'estimating') return 'estimate';
      if (tab === 'jobcosting') return 'jobcosting';
      if (tab === 'invoices') return 'invoices';
      if (tab === 'changeorders') return 'changeorders';
      if (tab === 'documents') return 'documents';
      if (tab === 'messages') return 'messages';
      if (tab === 'activity') return 'activity';
      if (tab === 'portals') return 'portals';
      if (tab === 'electrical') return 'electrical';
      if (tab === 'preengineering') return 'preengineering';
      if (tab === 'project' || tab === 'site') return 'projectinfo';
    }
    
    // Default for project routes without specific tab
    if (path.match(/^\/projects\/[^/]+/)) return 'project';
    
    // Pipeline/CRM pages
    if (path.includes('/pipeline') || path.includes('/crm') || path.includes('/leads')) return 'pipeline';
    
    // Contacts pages
    if (path.includes('/contacts') || path.includes('/contractors') || path.includes('/vendors')) return 'contacts';
    
    // Dashboard
    if (path.includes('/dashboard') || path === '/') return 'default';
    
    return 'default';
  }, [location]);
  
  const contextConfig = QUICK_ACTIONS[currentContext] || QUICK_ACTIONS.default;
  
  // Load settings from localStorage
  useEffect(() => {
    const savedY = localStorage.getItem('compass_button_y');
    if (savedY) setButtonY(parseFloat(savedY));
    
    const savedTab = localStorage.getItem('compass_last_tab');
    if (savedTab) setActiveTab(savedTab);
  }, []);
  
  // Save button position
  useEffect(() => {
    localStorage.setItem('compass_button_y', buttonY.toString());
  }, [buttonY]);
  
  // Save last tab
  useEffect(() => {
    localStorage.setItem('compass_last_tab', activeTab);
  }, [activeTab]);
  
  // Fetch project data for Journey tab
  useEffect(() => {
    if (isOpen && currentProjectId && activeTab === 'journey') {
      fetchProject();
      fetchEmailTemplates();
    }
  }, [isOpen, currentProjectId, activeTab]);
  
  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/api/projects/${currentProjectId}`);
      setProject(response.data);
      // Normalize legacy tasks[]-shaped phases so every downstream .steps read
      // (navigation, progress, completion PUTs) sees a uniform steps[] array.
      const fetchedPhases = (response.data.phases || []).map(p => ({ ...p, steps: getPhaseItems(p) }));
      setPhases(fetchedPhases);
      
      // Auto-navigate to first incomplete step
      let foundIncomplete = false;
      for (let pIdx = 0; pIdx < fetchedPhases.length && !foundIncomplete; pIdx++) {
        const phase = fetchedPhases[pIdx];
        const steps = phase.steps || [];
        for (let sIdx = 0; sIdx < steps.length; sIdx++) {
          const step = steps[sIdx];
          if (!step.completed && step.status !== 'completed' && step.status !== 'skipped') {
            setCurrentPhaseIndex(pIdx);
            setCurrentStepIndex(sIdx);
            foundIncomplete = true;
            break;
          }
        }
      }
      if (!foundIncomplete && fetchedPhases.length > 0) {
        const lastPhase = fetchedPhases[fetchedPhases.length - 1];
        setCurrentPhaseIndex(fetchedPhases.length - 1);
        setCurrentStepIndex((lastPhase.steps?.length || 1) - 1);
      }
    } catch (error) {
      console.error('Error fetching project:', error);
    }
  };
  
  const fetchEmailTemplates = async () => {
    try {
      const response = await axios.get(`${API}/api/journey-email-templates`);
      setEmailTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };
  
  // Button drag handlers - only start drag after holding for a moment
  const dragTimeoutRef = useRef(null);
  const isDraggingRef = useRef(false);
  const wasDraggingRef = useRef(false);
  
  const onButtonDragStart = (e) => {
    e.preventDefault();
    wasDraggingRef.current = false;
    // Start a timeout to enable dragging after 150ms hold
    dragTimeoutRef.current = setTimeout(() => {
      isDraggingRef.current = true;
      wasDraggingRef.current = true;
      setIsDraggingButton(true);
      document.addEventListener('mousemove', onButtonDragMove);
      document.addEventListener('mouseup', onButtonDragEnd);
    }, 150);
  };
  
  const onButtonMouseUp = () => {
    // Clear the timeout if released before drag starts
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
    }
  };

  const onButtonDragMove = (e) => {
    if (!isDraggingRef.current) return;
    const newY = (e.clientY / window.innerHeight) * 100;
    setButtonY(Math.max(10, Math.min(85, newY)));
  };

  const onButtonDragEnd = () => {
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
      dragTimeoutRef.current = null;
    }
    isDraggingRef.current = false;
    setIsDraggingButton(false);
    document.removeEventListener('mousemove', onButtonDragMove);
    document.removeEventListener('mouseup', onButtonDragEnd);
    
    // Reset wasDragging after a brief delay to allow click handler to check it
    setTimeout(() => {
      wasDraggingRef.current = false;
    }, 100);
  };
  
  // Handle button click - only toggle if not dragging
  const handleButtonClick = () => {
    // Don't open if we just finished dragging
    if (wasDraggingRef.current || isDraggingRef.current) {
      return;
    }
    setIsOpen(!isOpen);
  };
  
  // Journey calculations
  const currentPhase = phases[currentPhaseIndex];
  const currentStep = currentPhase?.steps?.[currentStepIndex];
  // Unified journey math — same formula as the Journey tab + AI Chad:
  // progress = completed / (total - notNeeded)
  const { progress: journeyProgress } = computeJourneyProgress(phases);
  
  // Find email template for step
  const findEmailTemplate = useCallback(() => {
    if (!currentStep) return null;
    const stepName = currentStep.name?.toLowerCase() || '';
    if (currentStep.action_type === 'email' || stepName.includes('email') || stepName.includes('send')) {
      return emailTemplates.find(t => 
        stepName.includes(t.trigger_step?.toLowerCase() || '')
      ) || emailTemplates[0];
    }
    return null;
  }, [currentStep, emailTemplates]);
  
  // Handle Quick Action click
  const handleAction = (action) => {
    const closeAfterAction = () => {
      setTimeout(() => setIsOpen(false), 100);
    };

    if (action.route) {
      let route = action.route;
      if (currentProjectId && !route.includes('?')) {
        route = `${route}?project=${currentProjectId}`;
      }
      navigate(route);
      closeAfterAction();
    } else if (action.action) {
      // Dispatch custom event for the action
      const eventDetail = { 
        action: action.action, 
        actionId: action.id,
        label: action.label,
        projectId: currentProjectId,
        context: currentContext 
      };
      
      window.dispatchEvent(new CustomEvent('quickAction', { detail: eventDetail }));
      
      // Handle specific actions
      switch (action.action) {
        case 'expense':
          window.dispatchEvent(new CustomEvent('openExpenseDialog', { detail: { projectId: currentProjectId } }));
          closeAfterAction();
          break;
        case 'rfp':
        case 'new-rfp':
          if (currentProjectId) {
            navigate(`/projects/${currentProjectId}/bids?action=new-rfp`);
          } else {
            toast.info('Please select a project first');
          }
          closeAfterAction();
          break;
        case 'contract':
          if (currentProjectId) {
            navigate(`/projects/${currentProjectId}/documents/contractor-agreement/new`);
          }
          closeAfterAction();
          break;
        case 'meeting':
          window.dispatchEvent(new CustomEvent('openMeetingDialog', { detail: { projectId: currentProjectId } }));
          closeAfterAction();
          break;
        case 'upload':
          window.dispatchEvent(new CustomEvent('openFileUpload', { detail: { projectId: currentProjectId } }));
          closeAfterAction();
          break;
        case 'clip':
        case 'new-clip':
          if (currentProjectId) {
            navigate(`/projects/${currentProjectId}/hub?tab=clips&action=new`);
          } else {
            navigate('/clips?action=new');
          }
          closeAfterAction();
          break;
        default:
          toast.info(`Action: ${action.label}`);
      }
    }
  };
  
  // Journey step handlers
  const handleCompleteStep = async () => {
    if (!currentStep || !currentProjectId) return;
    try {
      await axios.put(
        `${API}/api/projects/${currentProjectId}/phases/${currentPhase.id}/steps/${currentStep.id}`,
        { completed: true, status: 'completed', completed_at: new Date().toISOString() }
      );
      if (currentStepIndex < (currentPhase?.steps?.length || 0) - 1) {
        setCurrentStepIndex(prev => prev + 1);
      } else if (currentPhaseIndex < phases.length - 1) {
        setCurrentPhaseIndex(prev => prev + 1);
        setCurrentStepIndex(0);
      }
      toast.success('Step completed!');
      fetchProject();
    } catch (error) {
      toast.error('Failed to complete step');
    }
  };
  
  const executeNotNeeded = async () => {
    if (!currentStep || !currentProjectId) return;
    try {
      await axios.put(
        `${API}/api/projects/${currentProjectId}/phases/${currentPhase.id}/steps/${currentStep.id}`,
        { status: 'skipped', completed: false, skip_reason: notNeededReason.trim() || null }
      );
      setShowNotNeededDialog(false);
      if (currentStepIndex < (currentPhase?.steps?.length || 0) - 1) {
        setCurrentStepIndex(prev => prev + 1);
      } else if (currentPhaseIndex < phases.length - 1) {
        setCurrentPhaseIndex(prev => prev + 1);
        setCurrentStepIndex(0);
      }
      toast.info('Step marked not needed');
      fetchProject();
    } catch (error) {
      console.error('Error marking step not needed:', error);
      toast.error(error.response?.status === 403
        ? 'This step is locked. Unlock it from the Journey tab to make changes.'
        : 'Failed to mark step not needed');
    }
  };
  
  const handleNavigateAction = () => {
    if (!currentStep) return;
    const stepName = currentStep.name?.toLowerCase() || '';
    if (stepName.includes('takeoff') || stepName.includes('floor plan')) {
      navigate(`/projects/${currentProjectId}/floor-plans`);
    } else if (stepName.includes('survey')) {
      navigate(`/projects/${currentProjectId}/hub?tab=survey`);
    } else {
      navigate(`/projects/${currentProjectId}/hub`);
    }
  };
  
  // Email handlers
  const handleEmailAction = () => {
    const template = findEmailTemplate();
    if (template) {
      setSelectedTemplate(template);
      const client = project?.client || {};
      const firstName = (client.name || '').split(' ')[0] || 'Client';
      
      setEmailData({
        to: client.email || '',
        subject: (template.subject || '').replace(/\{\{client_first_name\}\}/g, firstName).replace(/\{\{project_name\}\}/g, project?.name || ''),
        body: (template.body || '').replace(/\{\{client_first_name\}\}/g, firstName).replace(/\{\{project_name\}\}/g, project?.name || '')
      });
      setScheduledDate(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
      setShowEmailPreview(true);
    }
  };
  
  const handleSendEmail = async () => {
    if (!emailData.to) {
      toast.error('Please enter a recipient email');
      return;
    }
    setSendingEmail(true);
    try {
      const payload = {
        to_email: emailData.to,
        subject: emailData.subject,
        body_text: emailData.body,
        template_id: selectedTemplate?.id,
        step_id: currentStep?.id,
        ...(scheduleEmail && { scheduled_for: `${scheduledDate}T${scheduledTime}:00` })
      };
      await axios.post(`${API}/api/projects/${currentProjectId}/send-wizard-email`, payload);
      toast.success(scheduleEmail ? 'Email scheduled!' : 'Email sent!');
      setShowEmailPreview(false);
      handleCompleteStep();
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };
  
  // Learn tab - filtered blueprints
  const filteredBlueprints = searchQuery
    ? allBlueprints.filter(b =>
        b.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.category?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];
  
  const StepIcon = getStepIcon(currentStep?.action_type);
  const hasEmailAction = findEmailTemplate() !== null;
  
  const templatesByCategory = {
    client: emailTemplates.filter(t => t.category === 'client'),
    contractor: emailTemplates.filter(t => t.category === 'contractor'),
    supplier: emailTemplates.filter(t => t.category === 'supplier')
  };

  // Help Icons Toggle Component
  const HelpIconsToggle = () => {
    const { helpIconsEnabled, toggleHelpIcons } = useHelpIcons();
    
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={toggleHelpIcons}
              className={`p-1.5 rounded transition-colors ${
                helpIconsEnabled 
                  ? 'bg-white/20 text-white' 
                  : 'hover:bg-white/10 text-white/60'
              }`}
            >
              {helpIconsEnabled ? (
                <Eye className="w-4 h-4" />
              ) : (
                <EyeOff className="w-4 h-4" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
            <p className="text-xs">
              {helpIconsEnabled ? 'Hide help icons (?)' : 'Show help icons (?)'}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Hide during consultation presentations
  if (location.pathname.startsWith('/consultation/')) return null;

  return (
    <>
      {/* Compass Button - Unified Side Tab (hidden when using FloatingTabGroup) */}
      {!hideTab && (
      <div
        ref={buttonRef}
        onClick={handleButtonClick}
        onMouseDown={onButtonDragStart}
        onMouseUp={onButtonMouseUp}
        className={`fixed right-0 z-[var(--km-z-rail)] flex flex-col items-center justify-center gap-1 px-1 py-2 border border-r-0 rounded-l-md transition-all duration-200 select-none ${
          isDraggingButton
            ? 'cursor-grabbing bg-[#70170F] border-[#70170F] scale-105'
            : isOpen 
              ? 'cursor-pointer bg-[#70170F] border-[#70170F]' 
              : 'cursor-pointer bg-[#FDFCFB]/95 border-[#70170F]/30 hover:bg-white hover:border-[#70170F]/50'
        }`}
        style={{ 
          top: `${buttonY}%`, 
          transform: 'translateY(-50%)',
          width: '28px'
        }}
        title="Click to open Compass • Hold and drag to reposition"
        data-testid="compass-button"
      >
        <Compass className={`w-4 h-4 ${isOpen || isDraggingButton ? 'text-white' : 'text-[#70170F]'}`} />
        <span 
          className={`font-medium whitespace-nowrap ${isOpen || isDraggingButton ? 'text-white' : 'text-[#70170F]'}`}
          style={{ 
            writingMode: 'vertical-lr', 
            textOrientation: 'mixed',
            fontSize: '9px',
            letterSpacing: '0.5px'
          }}
        >
          COMPASS
        </span>
        {/* Progress indicator ring */}
        {isProjectRoute && journeyProgress > 0 && !isOpen && (
          <div className="absolute -top-1 -left-1">
            <svg width="12" height="12" viewBox="0 0 12 12">
              <circle cx="6" cy="6" r="4" fill="none" stroke="#E5E2DD" strokeWidth="2" />
              <circle 
                cx="6" cy="6" r="4" fill="none" stroke="#70170F" strokeWidth="2"
                strokeDasharray={`${journeyProgress * 0.251} 25.1`}
                strokeLinecap="round"
                transform="rotate(-90 6 6)"
              />
            </svg>
          </div>
        )}
      </div>
      )}

      {/* Drawer Panel */}
      <div 
        className={`fixed top-0 right-0 h-full bg-[#0D0D0D] border-l border-[#2A2A2A] shadow-2xl z-[var(--km-z-drawer)] transition-transform duration-300 flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ width: '380px' }}
        data-testid="compass-drawer"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#70170F] to-[#BC5D41] px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <Compass className="w-5 h-5 text-white" />
            <span className="text-white font-semibold">Compass</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Help Icons Toggle */}
            <HelpIconsToggle />
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded hover:bg-white/20 text-white/80 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Context Breadcrumb */}
        <div className="px-4 py-2 border-b border-[#2A2A2A] bg-[#1A1A1A]/50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <MapPin className="w-3 h-3" />
              <span className="truncate">
                {location.pathname.replace(/\//g, ' > ').replace(/^>/, '').trim() || 'Home'}
              </span>
            </div>
            {isProjectRoute && (
              <Badge variant="outline" className="border-[#70170F]/50 text-[#BC5D41] text-[10px]">
                {journeyProgress}% complete
              </Badge>
            )}
          </div>
          {/* Mini progress bar */}
          {isProjectRoute && (
            <div className="h-1 bg-[#2A2A2A] rounded-full overflow-hidden mt-2">
              <div 
                className="h-full bg-gradient-to-r from-[#70170F] to-[#BC5D41] transition-all duration-500"
                style={{ width: `${journeyProgress}%` }}
              />
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="px-2 py-2 border-b border-[#2A2A2A] flex-shrink-0">
          <div className="flex gap-1">
            {TABS.map((tab) => {
              const TabIcon = tab.icon;
              const isActive = activeTab === tab.id;
              // Hide Journey tab if not on project route
              if (tab.id === 'journey' && !isProjectRoute) return null;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex flex-col items-center gap-1 px-2 py-2 rounded-lg text-xs font-medium transition-all ${
                    isActive 
                      ? 'bg-[#70170F] text-white' 
                      : 'text-gray-400 hover:text-white hover:bg-[#1A1A1A]'
                  }`}
                  title={tab.description}
                >
                  <TabIcon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <ScrollArea className="flex-1">
          <div className="p-4">
            {/* ACTIONS TAB */}
            {activeTab === 'actions' && (
              <div className="space-y-4">
                {/* Context Header with Description */}
                <div className="p-3 rounded-lg bg-gradient-to-r from-[#70170F]/20 to-[#BC5D41]/10 border border-[#BC5D41]/20">
                  <div className="flex items-center gap-2 mb-2">
                    <contextConfig.icon className="w-5 h-5 text-[#BC5D41]" />
                    <span className="text-white font-medium">{contextConfig.label}</span>
                  </div>
                  <p className="text-gray-400 text-xs leading-relaxed">
                    {contextConfig.description}
                  </p>
                </div>
                
                {/* Actions List */}
                <div className="space-y-2">
                  {contextConfig.actions.map((action) => {
                    const ActionIcon = action.icon;
                    return (
                      <TooltipProvider key={action.id}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              onClick={() => handleAction(action)}
                              className="group flex items-center justify-between p-3 rounded-lg bg-[#1A1A1A] hover:bg-[#2A2A2A] border border-[#2A2A2A] hover:border-[#BC5D41]/30 cursor-pointer transition-all"
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="w-9 h-9 rounded-lg bg-[#BC5D41]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#BC5D41]/20 transition-colors">
                                  <ActionIcon className="w-4 h-4 text-[#BC5D41]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <span className="text-white text-sm block">{action.label}</span>
                                  {action.description && (
                                    <span className="text-gray-500 text-xs block truncate">{action.description}</span>
                                  )}
                                </div>
                              </div>
                              <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-[#BC5D41] transition-colors flex-shrink-0 ml-2" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="left" className="max-w-xs">
                            <p className="text-sm">{action.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })}
                </div>
                
                {/* Tip */}
                <div className="flex items-start gap-2 p-3 rounded-lg bg-[#1A1A1A]/50 border border-[#2A2A2A]">
                  <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-500">
                    <span className="text-amber-500 font-medium">Tip:</span> Actions change based on where you are in KEY-MADE. Navigate to different pages to see relevant actions.
                  </p>
                </div>
              </div>
            )}

            {/* JOURNEY TAB */}
            {activeTab === 'journey' && isProjectRoute && (
              <div className="space-y-4">
                {/* Journey Header with Explanation */}
                <div className="p-3 rounded-lg bg-gradient-to-r from-[#70170F]/20 to-[#BC5D41]/10 border border-[#BC5D41]/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Route className="w-5 h-5 text-[#BC5D41]" />
                    <span className="text-white font-medium">Project Journey</span>
                  </div>
                  <p className="text-gray-400 text-xs leading-relaxed">
                    This is your project roadmap. Follow these steps in order to guide your project from start to finish. Each phase contains tasks that help move the project forward.
                  </p>
                </div>
                
                {/* Project Name */}
                <div className="text-white font-medium truncate">
                  {project?.name || 'Loading...'}
                </div>
                
                {/* Phase Tabs */}
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
                  {phases.map((phase, idx) => {
                    const phaseComplete = phase.steps?.every(s => s.completed || s.status === 'completed' || s.status === 'skipped');
                    return (
                      <button
                        key={phase.id}
                        onClick={() => { setCurrentPhaseIndex(idx); setCurrentStepIndex(0); }}
                        className={`flex-shrink-0 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                          idx === currentPhaseIndex 
                            ? 'bg-[#BC5D41] text-white' 
                            : phaseComplete
                              ? 'bg-green-900/30 text-green-400'
                              : 'bg-[#2A2A2A] text-gray-400 hover:text-white'
                        }`}
                      >
                        {phaseComplete && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                        {phase.name?.split(' ')[0]}
                      </button>
                    );
                  })}
                </div>

                {/* Current Step */}
                {currentStep ? (
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${
                        currentStep.completed || currentStep.status === 'completed'
                          ? 'bg-green-500' : 'bg-[#BC5D41]/20'
                      }`}>
                        {currentStep.completed || currentStep.status === 'completed' ? (
                          <CheckCircle2 className="w-5 h-5 text-white" />
                        ) : (
                          <StepIcon className="w-5 h-5 text-[#BC5D41]" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-white font-medium text-sm">{currentStep.name}</h4>
                        <p className="text-gray-500 text-xs">Step {currentStepIndex + 1} of {currentPhase?.steps?.length || 0}</p>
                      </div>
                    </div>

                    {currentStep.definition && (
                      <p className="text-gray-400 text-sm">{currentStep.definition}</p>
                    )}

                    {/* Step Actions */}
                    {currentStep.completed || currentStep.status === 'completed' ? (
                      <div className="p-3 rounded-lg bg-green-900/20 border border-green-700/30">
                        <div className="flex items-center gap-2 text-green-400 text-sm">
                          <CheckCircle2 className="w-4 h-4" />
                          <span className="font-medium">Completed</span>
                        </div>
                      </div>
                    ) : currentStep.status === 'skipped' ? (
                      <div className="p-3 rounded-lg bg-yellow-900/20 border border-yellow-700/30">
                        <div className="flex items-center gap-2 text-yellow-400 text-sm">
                          <SkipForward className="w-4 h-4" />
                          <span className="font-medium">Not needed</span>
                        </div>
                        {currentStep.skip_reason && (
                          <p className="text-yellow-400/70 text-xs italic mt-1">“{currentStep.skip_reason}”</p>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={handleCompleteStep} 
                          className="mt-2 border-yellow-700/50 text-yellow-400 hover:bg-yellow-900/30 text-xs"
                        >
                          Mark as Done Instead
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {hasEmailAction ? (
                          <Button onClick={handleEmailAction} className="w-full bg-[#BC5D41] hover:bg-[#70170F] text-sm">
                            <Mail className="w-4 h-4 mr-2" />
                            Compose & Send Email
                          </Button>
                        ) : currentStep.keymade_link ? (
                          <Button onClick={handleNavigateAction} className="w-full bg-[#BC5D41] hover:bg-[#70170F] text-sm">
                            <Play className="w-4 h-4 mr-2" />
                            Go to Task
                          </Button>
                        ) : (
                          <Button onClick={handleCompleteStep} className="w-full bg-green-600 hover:bg-green-700 text-sm">
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Mark Complete
                          </Button>
                        )}
                        <div className="flex gap-2">
                          {!hasEmailAction && (
                            <Button variant="outline" onClick={handleCompleteStep} className="flex-1 border-[#2A2A2A] text-gray-300 hover:bg-[#1A1A1A] text-xs">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Done
                            </Button>
                          )}
                          <Button variant="outline" onClick={() => { setNotNeededReason(''); setShowNotNeededDialog(true); }} data-testid="compass-not-needed-btn" className="flex-1 border-[#2A2A2A] text-gray-300 hover:bg-[#1A1A1A] text-xs">
                            <SkipForward className="w-3 h-3 mr-1" /> Not needed
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Step Navigation */}
                    <div className="flex justify-between pt-3 border-t border-[#2A2A2A]">
                      <button
                        onClick={() => {
                          if (currentStepIndex > 0) setCurrentStepIndex(prev => prev - 1);
                          else if (currentPhaseIndex > 0) {
                            setCurrentPhaseIndex(prev => prev - 1);
                            setCurrentStepIndex((phases[currentPhaseIndex - 1]?.steps?.length || 1) - 1);
                          }
                        }}
                        disabled={currentPhaseIndex === 0 && currentStepIndex === 0}
                        className="flex items-center text-xs text-gray-500 hover:text-white disabled:opacity-30"
                      >
                        <ChevronLeft className="w-4 h-4" /> Previous
                      </button>
                      <button
                        onClick={() => {
                          if (currentStepIndex < (currentPhase?.steps?.length || 0) - 1) setCurrentStepIndex(prev => prev + 1);
                          else if (currentPhaseIndex < phases.length - 1) {
                            setCurrentPhaseIndex(prev => prev + 1);
                            setCurrentStepIndex(0);
                          }
                        }}
                        disabled={currentPhaseIndex === phases.length - 1 && currentStepIndex === (currentPhase?.steps?.length || 0) - 1}
                        className="flex items-center text-xs text-gray-500 hover:text-white disabled:opacity-30"
                      >
                        Next <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-6">
                    <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No steps in this phase</p>
                  </div>
                )}
              </div>
            )}

            {/* LEARN TAB */}
            {activeTab === 'learn' && (
              <div className="space-y-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search help topics..."
                    className="pl-9 bg-[#1A1A1A] border-[#2A2A2A] text-white text-sm"
                  />
                </div>

                {/* Search Results */}
                {searchQuery && filteredBlueprints.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs text-gray-500 uppercase tracking-wider">Results</div>
                    {filteredBlueprints.slice(0, 6).map(bp => (
                      <button
                        key={bp.id}
                        onClick={() => {
                          fetchBlueprintById(bp.id);
                          setSearchQuery('');
                        }}
                        className="w-full text-left p-3 rounded-lg bg-[#1A1A1A] hover:bg-[#2A2A2A] border border-[#2A2A2A] transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span>{bp.icon}</span>
                          <span className="text-white text-sm">{bp.title}</span>
                        </div>
                        <span className="text-xs text-gray-500">{bp.category}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Current Page Blueprint */}
                {!searchQuery && (
                  <>
                    {routeBlueprint ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{routeBlueprint.icon}</span>
                          <h3 className="text-white font-medium">{routeBlueprint.title}</h3>
                        </div>
                        
                        {routeBlueprint.content?.overview && (
                          <p className="text-gray-400 text-sm leading-relaxed">
                            {routeBlueprint.content.overview}
                          </p>
                        )}

                        {routeBlueprint.content?.quickStart?.length > 0 && (
                          <div className="space-y-2">
                            <div className="text-xs text-gray-500 uppercase tracking-wider flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              Quick Start
                            </div>
                            {routeBlueprint.content.quickStart.map((step, i) => (
                              <div key={i} className="flex items-start gap-2 p-2 bg-[#1A1A1A] rounded-lg">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#70170F] text-white text-xs flex items-center justify-center">
                                  {step.step}
                                </span>
                                <span className="text-sm text-gray-300">{step.text}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {routeBlueprint.content?.proTips?.length > 0 && (
                          <div className="p-3 bg-amber-900/20 border border-amber-800/30 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <Lightbulb className="w-4 h-4 text-amber-500" />
                              <span className="text-xs font-medium text-amber-400">Pro Tips</span>
                            </div>
                            <ul className="space-y-1">
                              {routeBlueprint.content.proTips.map((tip, i) => (
                                <li key={i} className="text-sm text-amber-200/80 flex items-start gap-2">
                                  <span>•</span>
                                  <span>{tip}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <a 
                          href="/blueprints" 
                          className="flex items-center gap-1 text-xs text-[#BC5D41] hover:underline"
                        >
                          View all Blueprints
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No help content for this page yet.</p>
                        <p className="text-xs mt-1">Try searching for a topic above.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* MAP TAB */}
            {activeTab === 'map' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Map className="w-5 h-5 text-[#BC5D41]" />
                    <span className="text-white font-medium">System Map</span>
                  </div>
                  <a 
                    href="/guide" 
                    className="text-xs text-[#BC5D41] hover:underline flex items-center gap-1"
                  >
                    Full View <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                <p className="text-gray-400 text-sm">
                  Explore how KEY-MADE's features connect together. Click any node to learn more.
                </p>

                {/* Mini System Map Preview */}
                <div className="relative h-48 bg-[#1A1A1A] rounded-lg border border-[#2A2A2A] overflow-hidden">
                  {/* Simplified node preview */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="grid grid-cols-3 gap-3 p-4">
                      {[
                        { label: 'Projects', color: '#70170F' },
                        { label: 'Clips', color: '#BC5D41' },
                        { label: 'Estimates', color: '#F59E0B' },
                        { label: 'Bids', color: '#10B981' },
                        { label: 'POs', color: '#3B82F6' },
                        { label: 'Invoices', color: '#8B5CF6' },
                      ].map((node, i) => (
                        <div
                          key={i}
                          className="w-16 h-10 rounded-md flex items-center justify-center text-white text-[10px] font-medium shadow-lg cursor-pointer hover:scale-105 transition-transform"
                          style={{ backgroundColor: node.color }}
                          onClick={() => navigate('/guide')}
                        >
                          {node.label}
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Connecting lines (decorative) */}
                  <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-30">
                    <line x1="25%" y1="35%" x2="50%" y2="35%" stroke="#70170F" strokeWidth="1" />
                    <line x1="50%" y1="35%" x2="75%" y2="35%" stroke="#70170F" strokeWidth="1" />
                    <line x1="25%" y1="65%" x2="50%" y2="65%" stroke="#70170F" strokeWidth="1" />
                    <line x1="50%" y1="65%" x2="75%" y2="65%" stroke="#70170F" strokeWidth="1" />
                    <line x1="50%" y1="35%" x2="50%" y2="65%" stroke="#70170F" strokeWidth="1" />
                  </svg>
                </div>

                {/* Quick Links */}
                <div className="space-y-2">
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Quick Links</div>
                  {[
                    { label: 'Full System Blueprint', route: '/guide', icon: Map },
                    { label: 'Blueprint Library', route: '/blueprints', icon: BookOpen },
                    { label: 'Terms Glossary', route: '/glossary', icon: FileText },
                  ].map((link, i) => {
                    const LinkIcon = link.icon;
                    return (
                      <a
                        key={i}
                        href={link.route}
                        className="flex items-center gap-3 p-2 rounded-lg bg-[#1A1A1A] hover:bg-[#2A2A2A] border border-[#2A2A2A] transition-colors"
                      >
                        <LinkIcon className="w-4 h-4 text-[#BC5D41]" />
                        <span className="text-white text-sm">{link.label}</span>
                        <ArrowRight className="w-3 h-3 text-gray-500 ml-auto" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-[#2A2A2A] bg-[#0D0D0D] flex-shrink-0">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Press Esc to close</span>
            <a href="/guide" className="text-[#BC5D41] hover:underline flex items-center gap-1">
              Learning Center <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>

      {/* Overlay when open */}
      {isOpen && (
        <div 
          /* DP106 Part 5a — THE SCRIM STOPS AT THE RAIL'S EDGE. `inset-0` covered the
             whole viewport, including the rail, so while the compass was open no rail
             box could be hovered or clicked and Chad could not navigate. A scrim that
             holds the PAGE still is right; one that holds the NAVIGATION still is not.
             It now starts at --km-rail-w and the rail stays live. */
          className="fixed inset-y-0 right-0 bg-black/20 z-[var(--km-z-drawer)]"
          style={{ left: 'var(--km-rail-w, 0px)' }} 
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Email Dialog */}
      <Dialog open={showEmailPreview} onOpenChange={setShowEmailPreview}>
        <DialogContent className="sm:max-w-2xl bg-[#0D0D0D] border-[#1A1A1A] text-white max-h-[90vh] overflow-y-auto z-[10000]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#BC5D41]" />
              Compose Email
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              Send or schedule this email
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="flex gap-2 p-1 bg-[#1A1A1A] rounded-lg">
              {[
                { id: 'client', label: 'Client', icon: Users },
                { id: 'contractor', label: 'Contractor', icon: Building2 },
                { id: 'supplier', label: 'Supplier', icon: Truck }
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setRecipientType(id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors ${
                    recipientType === id ? 'bg-[#BC5D41] text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <Icon className="w-4 h-4" />{label}
                </button>
              ))}
            </div>

            <div>
              <Label className="text-gray-300 mb-1 block">Template</Label>
              <Select value={selectedTemplate?.id || ''} onValueChange={(id) => {
                const t = emailTemplates.find(x => x.id === id);
                if (t) {
                  setSelectedTemplate(t);
                  const firstName = (project?.client?.name || '').split(' ')[0] || 'Client';
                  setEmailData({
                    ...emailData,
                    subject: (t.subject || '').replace(/\{\{client_first_name\}\}/g, firstName).replace(/\{\{project_name\}\}/g, project?.name || ''),
                    body: (t.body || '').replace(/\{\{client_first_name\}\}/g, firstName).replace(/\{\{project_name\}\}/g, project?.name || '')
                  });
                }
              }}>
                <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                  <SelectValue placeholder="Select template..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white z-[10001]">
                  {templatesByCategory[recipientType]?.map(t => (
                    <SelectItem key={t.id} value={t.id} className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A] focus:text-white">{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-gray-300 mb-1 block">To</Label>
              <Input
                type="email"
                value={emailData.to}
                onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
                placeholder="recipient@email.com"
                className="bg-[#1A1A1A] border-[#2A2A2A]"
              />
            </div>

            <div>
              <Label className="text-gray-300 mb-1 block">Subject</Label>
              <Input
                value={emailData.subject}
                onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                className="bg-[#1A1A1A] border-[#2A2A2A]"
              />
            </div>

            <div>
              <Label className="text-gray-300 mb-1 block">Message</Label>
              <Textarea
                value={emailData.body}
                onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
                rows={8}
                className="bg-[#1A1A1A] border-[#2A2A2A] font-mono text-sm"
              />
            </div>

            <div className="p-3 rounded-lg bg-[#1A1A1A] border border-[#2A2A2A]">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <CalendarClock className="w-4 h-4 text-[#BC5D41]" />
                  <Label className="text-gray-300 text-sm">Schedule for later</Label>
                </div>
                <Switch checked={scheduleEmail} onCheckedChange={setScheduleEmail} />
              </div>
              {scheduleEmail && (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Date</Label>
                    <Input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="bg-[#0D0D0D] border-[#2A2A2A]" />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 mb-1 block">Time</Label>
                    <Input type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} className="bg-[#0D0D0D] border-[#2A2A2A]" />
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailPreview(false)} className="border-[#2A2A2A]">Cancel</Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail || !emailData.to} className="bg-[#BC5D41] hover:bg-[#70170F]">
              {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : scheduleEmail ? <CalendarClock className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {scheduleEmail ? 'Schedule' : 'Send Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Not Needed Dialog — same pattern as JourneySteps */}
      <AlertDialog open={showNotNeededDialog} onOpenChange={setShowNotNeededDialog}>
        <AlertDialogContent className="bg-[#1A1A1A] border-[#2A2A2A]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Mark this step as not needed?</AlertDialogTitle>
            <AlertDialogDescription>
              The step will be set aside and won&apos;t count against progress. You can restore it later.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {currentStep?.is_critical && (
            <div className="p-3 rounded-lg border bg-amber-900/20 border-amber-800" data-testid="compass-foundational-warning">
              <p className="text-sm font-medium text-amber-400">
                <Star className="w-4 h-4 inline mr-1 text-amber-500 fill-amber-500" />
                This is a foundational step: {currentStep?.name}. Continue anyway?
              </p>
            </div>
          )}

          <div className="py-2">
            <Label className="text-xs text-gray-500">Reason (optional)</Label>
            <Input
              value={notNeededReason}
              onChange={(e) => setNotNeededReason(e.target.value)}
              placeholder="e.g. Client already provided this"
              className="mt-1 bg-[#0D0D0D] border-[#2A2A2A] text-white"
              data-testid="compass-not-needed-reason-input"
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#2A2A2A]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeNotNeeded} className="bg-gray-600 hover:bg-gray-700" data-testid="compass-confirm-not-needed-btn">
              <SkipForward className="w-4 h-4 mr-2" />
              Mark Not Needed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
